import { Button, IconButton, InputAdornment, TextField } from "@mui/material";
import React, { Fragment, useContext, useEffect, useState } from "react";
import { FiPlus } from "react-icons/fi";
import { PagedDataView } from "../../lists/PagedDataView/PagedDataView";
import { api, IPackageList, Package } from "../../../logic/api";

import SearchIcon from "@mui/icons-material/Search";
import SwitchField from "../../editable/EditableField/fields/SwitchField";
import flex from "../../../styles/flex";
import { isAdmin } from "../../../utility/utils";
import { SessionContext } from "../../../utility/useSession";
import PackageListItem from "../../lists/listItems/PackageListItem/PackageListItem";
import PackageBuilder from "../builder/PackageBuilder";
import { useTranslation } from "react-i18next";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { useReleaseManagement, ReleaseFlag } from "@/utility/feature-management";

const PackageManager = () => {
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [showCreatePackage, setShowCreatePackage] = useState<boolean>(false);
  const [packageListFetchParams, setPackageListFetchParams] = useState<IPackageList>({
    includeExpired: true,
    includePreBookOnly: true,
  });
  const [filter, setFilter] = useState("");
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const { hasPermission } = useUserPermissions();
  const isRbacReleased = useReleaseManagement(ReleaseFlag.releaseRbac);

  const PackageDialog = () => {
    return <PackageBuilder packageId={selectedPackage == null ? null : selectedPackage.id} show={showCreatePackage} onClose={(didUpdate) => {
      setSelectedPackage(null);
      setShowCreatePackage(false);
      if (didUpdate == true) {
        setPackageListFetchParams({ ...packageListFetchParams });
      }
    }} />;
  };



  const carParkId = (session.current.site == null || session.current.site.home == null) ? null : session.current.site.home.id;
  const [currentCarParkId, setCurrentCarParkId] = useState(carParkId);

  useEffect(() => {
    if (carParkId != currentCarParkId) {
      setCurrentCarParkId(carParkId);
      setPackageListFetchParams({ ...packageListFetchParams });
    }
  }, [carParkId]);

  return (
    <div>
      <PackageDialog />
      <div style={{ padding: 10, textAlign: "right" }}>
        <div style={flex.flexRows}>
          <div style={flex.flexAuto}>
            <TextField
              autoFocus={false}
              label={t("packages.searchPackages")}
              variant="outlined"
              size="small"
              type="search"
              value={filter}
              onChange={(e) => {
                setFilter(e.currentTarget.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  if (e.currentTarget) {
                    e.currentTarget.blur();
                    setPackageListFetchParams({ ...packageListFetchParams, filter: filter });
                  }
                  return false;
                }

              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position={"end"}>
                    <IconButton
                      style={{ padding: 0 }}
                      onClick={() => {
                        setPackageListFetchParams({ ...packageListFetchParams, filter: filter });
                      }}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              data-testid="packages-search-packages-field"
            />
          </div>
          {isAdmin(session) && (
            <Fragment>
              <div style={flex.flexAuto}>
                <SwitchField onChange={(includeInactive) => {
                  setPackageListFetchParams({ ...packageListFetchParams, includeInactive: includeInactive });
                }} value={packageListFetchParams.includeInactive} data-testid="packages-include-inactive-switch" ></SwitchField>
              </div>
              <div style={flex.flexAuto}>
                <div style={{ textAlign: "right", paddingTop: 10, color: "grey" }}>
                  {t("packages.incInactive")}
                </div>
              </div>
              <div style={flex.flexAuto}>
                <SwitchField onChange={(includeExpired) => {
                  setPackageListFetchParams({ ...packageListFetchParams, includeExpired: includeExpired });
                }} value={packageListFetchParams.includeExpired} data-testid="packages-include-expired-switch" ></SwitchField>
              </div>
              <div style={flex.flexAuto}>
                <div style={{ textAlign: "right", paddingTop: 10, color: "grey" }}>
                  {t("packages.incExpired")}
                </div>
              </div>
            </Fragment>
          )}


          <div style={flex.flexFill}>

          </div>
          {(isRbacReleased ? hasPermission("bookingManagement.saleItems.create") : isAdmin(session)) && (
            <div style={flex.flexAuto}>
              <Button style={{ marginLeft: 10 }} variant="contained" color="secondary" data-testid="packages-new-package-button" onClick={() => {
                setSelectedPackage(null)
                setShowCreatePackage(true);
              }}><FiPlus style={{ marginRight: 10 }} />
                {t("packages.new")}
              </Button>
            </div>
          )}

        </div>
      </div>
      <PagedDataView<IPackageList, Package>
        apiHook={api.hooks.packages.list}
        parameters={packageListFetchParams}
        renderItem={(item) => {
          return <PackageListItem package={item} onEditPackage={(pkg) => {
            setSelectedPackage(pkg);
            setShowCreatePackage(true);
          }} data-testid="packages-package-list-item"></PackageListItem>
        }}>
        <div style={{ padding: 20, backgroundColor: "#eee" }}>
          {t("packages.noPackages")}
        </div>
      </PagedDataView>
    </div >
  );
}

export default PackageManager;
